/**
 * openTemplatePickerDialog.ts — open "Choose up to 3 templates" as an Office
 * Dialog, hand it the ranked candidates, and return what the user picked.
 *
 * Follows `openCreateSlidesDialog.ts`: a large window acting as its own scrim,
 * with a centred card floated inside by CSS. The difference is direction of
 * travel — Create Slides collects input from nothing, whereas this picker has
 * to be GIVEN the candidates the pane just retrieved. A dialog can't fetch them
 * itself: ranking needs the active slide's XML, and only the pane can call
 * PowerPoint.run.
 *
 * So the pane pushes them with `Dialog.messageChild`, which needs DialogApi 1.2.
 * The dialog registers its listener and then posts "ready"; only then does the
 * payload go out, so a fast reply cannot arrive before anything is listening.
 * If 1.2 is missing the picker would open empty, which is worse than not
 * opening at all — `canUseTemplatePickerDialog()` reports that up front so the
 * caller can keep the picker in the pane instead.
 */

import {
  TEMPLATE_PICKER_DIALOG_READY,
  type TemplatePickerDialogOutbound,
  type TemplatePickerDialogResult,
} from "../services/templatePickerDialogMessages";
import type { TemplateCandidate } from "../services/slideTransformTemplateApi";
import { logSlideTransformError } from "../services/slideTransformTelemetry";

/**
 * Window size as a PERCENTAGE of the screen (the Office Dialog API accepts
 * nothing else). Wide enough for the picker's four-column card grid at a size
 * where the thumbnails and match % read clearly — the point of moving it out of
 * the pane — and matched to the Create Slides wizard so the two large Deckorate
 * modals open at the same scale.
 */
const DIALOG_SIZE = { height: 80, width: 82 } as const;

/** Relative path of the dialog page served from the add-in origin. */
const DIALOG_PAGE = "/transform-template-dialog.html";

/**
 * Whether the picker can run as a dialog here: the Dialog API must exist AND
 * support `messageChild` (DialogApi 1.2), without which the candidates could
 * never reach the window.
 */
export function canUseTemplatePickerDialog(): boolean {
  if (typeof Office === "undefined") return false;
  if (typeof Office.context?.ui?.displayDialogAsync !== "function") return false;
  // isSetSupported is itself absent in some older hosts — treat that as "no".
  return Office.context.requirements?.isSetSupported?.("DialogApi", "1.2") === true;
}

/**
 * Show the picker and resolve with the user's decision.
 *
 * Resolves `{ kind: "cancel" }` when the window is closed without choosing, and
 * rejects with a user-facing message only when the window cannot be opened at
 * all — so the caller can tell "changed their mind" from "this surface is
 * broken" and fall back accordingly.
 */
export function openTemplatePickerDialog(
  candidates: readonly TemplateCandidate[],
): Promise<TemplatePickerDialogResult> {
  return new Promise((resolve, reject) => {
    if (!canUseTemplatePickerDialog()) {
      reject(new Error("The template window isn't available in this version of PowerPoint."));
      return;
    }

    const url = `${globalThis.location.origin}${DIALOG_PAGE}`;
    Office.context.ui.displayDialogAsync(url, { ...DIALOG_SIZE }, (asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        const error = asyncResult.error;
        logSlideTransformError(
          "slide-transform-template-dialog-open",
          `Office failed to open the template picker (code ${error?.code ?? "?"}): ${error?.message ?? "unknown error"}`,
        );
        reject(new Error("Couldn't open the template window. Please try again."));
        return;
      }

      const dialog = asyncResult.value;
      let settled = false;
      const settle = (result: TemplatePickerDialogResult) => {
        if (settled) return;
        settled = true;
        try {
          dialog.close();
        } catch {
          /* already closed by the host */
        }
        resolve(result);
      };

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
        let parsed: TemplatePickerDialogOutbound;
        try {
          parsed = JSON.parse((arg as { message: string }).message) as TemplatePickerDialogOutbound;
        } catch (error) {
          logSlideTransformError(
            "slide-transform-template-dialog-message",
            `Malformed message from the template picker: ${error instanceof Error ? error.message : String(error)}`,
          );
          return; // Keep the window open; one bad frame shouldn't close it.
        }

        if (parsed.type === TEMPLATE_PICKER_DIALOG_READY) {
          // The listener is up — now the payload can't be missed.
          dialog.messageChild(JSON.stringify({ type: "candidates", candidates }));
          return;
        }
        if (parsed.type === "apply") {
          settle({ kind: "apply", picks: parsed.picks, fitMode: parsed.fitMode });
          return;
        }
        if (parsed.type === "use-ai") {
          settle({ kind: "use-ai" });
          return;
        }
        settle({ kind: "cancel" });
      });

      // X, Esc, or the host closing the window — resolve so the caller can't hang.
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => settle({ kind: "cancel" }));
    });
  });
}
